import React from "react";
import "@testing-library/jest-dom";
import { render, screen, fireEvent } from "@testing-library/react";
import { StructureNavigator } from "./StructureNavigator";
import type { StructureNavNode } from "./types";

const STORAGE_KEY = "test_structure_nav_open";

const nodes: StructureNavNode[] = [
  {
    id: "1",
    title: "Introduction",
    children: [],
  },
  {
    id: "2",
    title: "Background Analysis",
    children: [{ id: "3", title: "Background – Thinking", children: [] }],
  },
];

// jsdom implements neither of these; the widget uses both.
const mockIntersectionObserver = () => {
  (window as any).IntersectionObserver = class {
    observe() {}
    unobserve() {}
    disconnect() {}
  };
};

/** Drives the min-width query the component uses to pick its layout. */
const setViewport = (wide: boolean) => {
  window.matchMedia = ((query: string) => ({
    matches: query.includes("min-width") ? wide : false,
    media: query,
    addEventListener: () => {},
    removeEventListener: () => {},
  })) as any;
};

const renderSectionAnchors = () => {
  document.body.innerHTML = `
    <div data-nav-section-id="1"><input data-section-title-input="true" /></div>
    <div data-nav-section-id="2"><input data-section-title-input="true" /></div>
    <div data-nav-section-id="3"><input data-section-title-input="true" /></div>
  `;
};

beforeEach(() => {
  localStorage.clear();
  mockIntersectionObserver();
  window.scrollTo = jest.fn() as any;
  setViewport(true);
});

describe("StructureNavigator", () => {
  it("renders nothing when there are no sections", () => {
    const { container } = render(
      <StructureNavigator nodes={[]} storageKey={STORAGE_KEY} />,
    );
    expect(container).toBeEmptyDOMElement();
  });

  it("renders the tree, nested children included", () => {
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    expect(screen.getByText("Introduction")).toBeInTheDocument();
    expect(screen.getByText("Background Analysis")).toBeInTheDocument();
    expect(screen.getByText("Background – Thinking")).toBeInTheDocument();
  });

  it("scrolls the window to the section when a name is clicked", () => {
    renderSectionAnchors();
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    fireEvent.click(screen.getByText("Introduction"));

    expect(window.scrollTo).toHaveBeenCalledWith(
      expect.objectContaining({ behavior: "smooth" }),
    );
  });

  it("runs onNavigate after scrolling", () => {
    renderSectionAnchors();
    const onNavigate = jest.fn();
    render(
      <StructureNavigator
        nodes={nodes}
        storageKey={STORAGE_KEY}
        onNavigate={onNavigate}
      />,
    );

    fireEvent.click(screen.getByText("Background – Thinking"));

    expect(onNavigate).toHaveBeenCalledWith("3");
  });

  it("does not scroll when the section element is missing", () => {
    document.body.innerHTML = "";
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    fireEvent.click(screen.getByText("Introduction"));

    expect(window.scrollTo).not.toHaveBeenCalled();
  });

  it("toggles a subtree without navigating", () => {
    renderSectionAnchors();
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    fireEvent.click(screen.getByLabelText("Collapse section"));

    expect(screen.queryByText("Background – Thinking")).not.toBeInTheDocument();
    // Collapsing is not navigation.
    expect(window.scrollTo).not.toHaveBeenCalled();

    fireEvent.click(screen.getByLabelText("Expand section"));
    expect(screen.getByText("Background – Thinking")).toBeInTheDocument();
  });

  it("collapses the rail and persists that choice", () => {
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    fireEvent.click(screen.getByLabelText("Hide outline"));

    expect(screen.queryByText("Introduction")).not.toBeInTheDocument();
    expect(screen.getByLabelText("Show outline")).toBeInTheDocument();
    expect(localStorage.getItem(STORAGE_KEY)).toBe("0");
  });

  it("restores a collapsed rail from storage", () => {
    localStorage.setItem(STORAGE_KEY, "0");
    render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

    expect(screen.queryByText("Introduction")).not.toBeInTheDocument();

    fireEvent.click(screen.getByLabelText("Show outline"));

    expect(screen.getByText("Introduction")).toBeInTheDocument();
    expect(localStorage.getItem(STORAGE_KEY)).toBe("1");
  });

  describe("responsive defaults", () => {
    it("opens by default where there is room for a column", () => {
      setViewport(true);
      render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

      expect(screen.getByText("Introduction")).toBeInTheDocument();
    });

    it("starts collapsed on narrow screens so it does not push content down", () => {
      setViewport(false);
      render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

      expect(screen.queryByText("Introduction")).not.toBeInTheDocument();
      expect(screen.getByLabelText("Show outline")).toBeInTheDocument();
    });

    it("honours a stored choice over the viewport default", () => {
      setViewport(false);
      localStorage.setItem(STORAGE_KEY, "1");
      render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

      expect(screen.getByText("Introduction")).toBeInTheDocument();
    });

    it("closes itself after navigating on a narrow screen", () => {
      setViewport(false);
      localStorage.setItem(STORAGE_KEY, "1");
      renderSectionAnchors();
      render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

      fireEvent.click(screen.getByText("Introduction"));

      expect(screen.queryByText("Introduction")).not.toBeInTheDocument();
    });

    it("stays open after navigating on a wide screen", () => {
      setViewport(true);
      renderSectionAnchors();
      render(<StructureNavigator nodes={nodes} storageKey={STORAGE_KEY} />);

      fireEvent.click(screen.getByText("Introduction"));

      expect(screen.getByText("Introduction")).toBeInTheDocument();
    });
  });

  describe('sideColumnFrom="never"', () => {
    it("starts collapsed even where a column would fit", () => {
      setViewport(true);
      render(
        <StructureNavigator
          nodes={nodes}
          storageKey={STORAGE_KEY}
          sideColumnFrom="never"
        />,
      );

      expect(screen.queryByText("Introduction")).not.toBeInTheDocument();
      expect(screen.getByLabelText("Show outline")).toBeInTheDocument();
    });

    it("closes after navigating even on a wide screen", async () => {
      setViewport(true);
      localStorage.setItem(STORAGE_KEY, "1");
      renderSectionAnchors();
      render(
        <StructureNavigator
          nodes={nodes}
          storageKey={STORAGE_KEY}
          sideColumnFrom="never"
        />,
      );

      fireEvent.click(screen.getByText("Introduction"));

      expect(screen.queryByText("Introduction")).not.toBeInTheDocument();
      // Stacked above the content, the scroll is deferred one frame so it can
      // measure the target after the rail collapses.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      expect(window.scrollTo).toHaveBeenCalled();
    });

    it("still honours a stored open preference on mount", () => {
      setViewport(true);
      localStorage.setItem(STORAGE_KEY, "1");
      render(
        <StructureNavigator
          nodes={nodes}
          storageKey={STORAGE_KEY}
          sideColumnFrom="never"
        />,
      );

      expect(screen.getByText("Introduction")).toBeInTheDocument();
    });
  });
});
